import type { FormContentItem, FormValues } from '@/components/Form/types';
import type { FormStoryblok } from '@/types/component-types-sb';

const NON_VALUE_COMPONENTS = new Set(['NewLine', 'FormText', 'FormPage', 'FormSection', 'FormFieldset']);

export const getFormValueId = (item: FormContentItem): string | undefined => {
  if (item.component === 'FormMarketingOptIn') {
    return 'marketing_opt_in';
  }

  return !item.component || NON_VALUE_COMPONENTS.has(item.component) ? undefined : item.id;
};

export type FieldKeyEntry = {
  stateKey: string;
  valueId: string;
  item: FormContentItem;
};

export type FormFieldKeyRegistry = {
  getStateKey: (item: FormContentItem) => string;
  getValueId: (item: FormContentItem) => string | undefined;
  getLabelStateKey: (item: FormContentItem) => string;
  readValue: (values: FormValues, item: FormContentItem) => unknown;
  readLabelValue: (values: FormValues, item: FormContentItem) => unknown;
  getStateKeysForValueId: (valueId: string) => string[];
  getValueIdForStateKey: (stateKey: string) => string | undefined;
  getItemForStateKey: (stateKey: string) => FormContentItem | undefined;
  isStateKey: (key: string) => boolean;
  isRegisteredKey: (key: string) => boolean;
  forEachField: (visitor: (entry: FieldKeyEntry) => void) => void;
};

const registryCache = new WeakMap<FormStoryblok, FormFieldKeyRegistry>();

const collectValueFields = (items: FormContentItem[] | undefined, collected: FormContentItem[]): void => {
  if (!items) return;

  for (const item of items) {
    const valueId = getFormValueId(item);
    if (valueId) {
      collected.push(item);
    }

    if (['FormPage', 'FormSection', 'FormFieldset'].includes(item.component || '')) {
      collectValueFields(item.content || item.fields, collected);
    }
  }
};

export const buildFormFieldKeyRegistry = (form: FormStoryblok): FormFieldKeyRegistry => {
  const fields: FormContentItem[] = [];
  form.content?.forEach((page) => collectValueFields(page.content, fields));

  const valueIdCounts = new Map<string, number>();
  for (const item of fields) {
    const valueId = getFormValueId(item)!;
    valueIdCounts.set(valueId, (valueIdCounts.get(valueId) || 0) + 1);
  }

  const stateKeyByUid = new Map<string, string>();
  const stateKeyToValueId = new Map<string, string>();
  const valueIdToStateKeys = new Map<string, string[]>();
  const itemByStateKey = new Map<string, FormContentItem>();
  const entries: FieldKeyEntry[] = [];

  for (const item of fields) {
    const valueId = getFormValueId(item)!;
    const isDuplicate = (valueIdCounts.get(valueId) || 0) > 1;
    const stateKey = isDuplicate ? item._uid || valueId : valueId;

    if (item._uid) {
      stateKeyByUid.set(item._uid, stateKey);
    }
    stateKeyToValueId.set(stateKey, valueId);
    itemByStateKey.set(stateKey, item);
    valueIdToStateKeys.set(valueId, [...(valueIdToStateKeys.get(valueId) || []), stateKey]);
    entries.push({ stateKey, valueId, item });
  }

  const getStateKey = (item: FormContentItem): string => {
    const valueId = getFormValueId(item);
    if (!valueId) {
      return item._uid || '';
    }
    if (item._uid && stateKeyByUid.has(item._uid)) {
      return stateKeyByUid.get(item._uid)!;
    }
    return (valueIdCounts.get(valueId) || 0) > 1 ? item._uid || valueId : valueId;
  };

  return {
    getStateKey,
    getValueId: getFormValueId,
    getLabelStateKey: (item) => `${getStateKey(item)}__label`,
    readValue: (values, item) => values[getStateKey(item)],
    readLabelValue: (values, item) => values[`${getStateKey(item)}__label`],
    getStateKeysForValueId: (valueId) => valueIdToStateKeys.get(valueId) || [],
    getValueIdForStateKey: (stateKey) => stateKeyToValueId.get(stateKey),
    getItemForStateKey: (stateKey) => itemByStateKey.get(stateKey),
    isStateKey: (key) => stateKeyToValueId.has(key),
    isRegisteredKey: (key) => {
      if (stateKeyToValueId.has(key)) return true;
      if (key.endsWith('__label')) {
        return stateKeyToValueId.has(key.slice(0, -'__label'.length));
      }
      return false;
    },
    forEachField: (visitor) => {
      entries.forEach(visitor);
    },
  };
};

export const getFormFieldKeyRegistry = (form: FormStoryblok): FormFieldKeyRegistry => {
  const cached = registryCache.get(form);
  if (cached) {
    return cached;
  }

  const registry = buildFormFieldKeyRegistry(form);
  registryCache.set(form, registry);
  return registry;
};
